import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { scenarioStorage, LocalScenarioProcedure } from '@/lib/scenarioStorage';
import { CodeLookupResult } from './ScenarioCodeLookup';
import { ScenarioVisibilityToggle } from './ScenarioVisibilityToggle';
import { ScenarioReviewPanel } from './ScenarioReviewPanel';
import { ScenarioTimeline } from './ScenarioTimeline';
import { CostDashboard } from './CostDashboard';
import { SmartSuggestions } from './SmartSuggestions';
import { ProcedureForm, ProcedureFormToggle } from './ProcedureForm';
import { NegotiationSheet } from './NegotiationSheet';
import { Plus, X, Save, Share2, ArrowLeft, Heart, Baby, Ambulance, Activity, Pill } from 'lucide-react';
import { toast } from 'sonner';
import { useBrand } from '@/config/brands';
import { useTheme } from '@/contexts/ThemeContext';
import { FamilyMemberPills } from '@/components/family/FamilyMemberPills';
import type { ScenarioTemplateData } from '@/components/CreateScenarioDialog';
import type { BundleSuggestion } from '@/lib/procedureBundles';

interface ManualScenarioBuilderProps {
  onBack: () => void;
  onCreated?: (scenarioId: string) => void;
  onClose: () => void;
  onStepChange?: (step: number) => void;
  templateData?: ScenarioTemplateData;
  initialFamilyMemberId?: string | null;
}

const ICON_OPTIONS = [
  { icon: Heart, label: 'Heart', color: '#ef4444' },
  { icon: Baby, label: 'Baby', color: '#4ade80' },
  { icon: Ambulance, label: 'Ambulance', color: '#f59e0b' },
  { icon: Activity, label: 'Activity', color: '#3b82f6' },
  { icon: Pill, label: 'Pill', color: '#8b5cf6' },
];

export function ManualScenarioBuilder({ onBack, onCreated, onClose, onStepChange, templateData, initialFamilyMemberId }: ManualScenarioBuilderProps) {
  const { t } = useTranslation(['scenarios', 'common']);
  const brand = useBrand();
  const isAhp = brand.key === 'ahp';
  const { isDark } = useTheme();
  const ahpDark = isAhp && isDark;
  const [step, setStep] = useState(templateData ? 2 : 1);

  // Notify parent of step changes
  useEffect(() => {
    onStepChange?.(step);
  }, [step, onStepChange]);

  // Step 1: Basic Info
  const [name, setName] = useState(templateData ? `${templateData.name} ${t('builder.myCopy')}` : '');
  const [description, setDescription] = useState(templateData?.description ?? '');
  const [duration, setDuration] = useState(templateData?.duration ?? '');
  const [tags, setTags] = useState<string[]>(templateData?.tags ?? []);
  const [tagInput, setTagInput] = useState('');
  const [selectedIconIndex, setSelectedIconIndex] = useState(0);
  const [severityLevel, setSeverityLevel] = useState(5);
  const [familyMemberId, setFamilyMemberId] = useState<string | null>(initialFamilyMemberId ?? null);

  // Step 2: Procedures
  const [procedures, setProcedures] = useState<LocalScenarioProcedure[]>(
    templateData?.procedures?.map((p) => ({
      code: p.code,
      codeType: p.codeType,
      description: p.description,
      phase: p.phase,
      quantity: p.quantity,
      estimatedCost: p.estimatedCost,
      setting: p.setting,
    })) ?? []
  );

  // Individual procedure form state
  const [procedureCode, setProcedureCode] = useState('');
  const [procedureCodeType, setProcedureCodeType] = useState('CPT');
  const [procedureDescription, setProcedureDescription] = useState('');
  const [procedurePhase, setProcedurePhase] = useState(t('builder.defaultPhase'));
  const [procedureQuantity, setProcedureQuantity] = useState('1');
  const [procedureCost, setProcedureCost] = useState('');
  const [procedureSetting, setProcedureSetting] = useState<'inpatient' | 'outpatient' | 'unspecified'>('unspecified');
  const [showAddForm, setShowAddForm] = useState(!templateData);

  // Step 3: Visibility
  const [visibility, setVisibility] = useState<'public' | 'unlisted'>('unlisted');
  const [isSaving, setIsSaving] = useState(false);

  // Editing state
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  // Derive phases from procedures
  const activePhasesFromData = Array.from(new Set(procedures.map(p => p.phase).filter(Boolean)));
  const phases = activePhasesFromData.length > 0 ? activePhasesFromData : ['Treatment'];

  const handleCodeSelected = (result: CodeLookupResult) => {
    setProcedureCode(result.code);
    setProcedureCodeType(result.codeType);
    setProcedureDescription(result.consumerName || result.description);
    if (result.medianPrice > 0) {
      setProcedureCost(result.medianPrice.toFixed(2));
    }
  };

  const resetProcedureForm = () => {
    setProcedureCode('');
    setProcedureDescription('');
    setProcedurePhase(t('builder.defaultPhase'));
    setProcedureQuantity('1');
    setProcedureCost('');
    setProcedureSetting('unspecified');
    setEditingIndex(null);
  };

  const handleAddProcedure = () => {
    if (!procedureCode.trim() || !procedureDescription.trim()) {
      toast.error(t('builder.errorCodeAndDescription'));
      return;
    }

    const newProcedure: LocalScenarioProcedure = {
      code: procedureCode.trim(),
      codeType: procedureCodeType,
      description: procedureDescription.trim(),
      phase: procedurePhase,
      quantity: parseInt(procedureQuantity) || 1,
      estimatedCost: parseFloat(procedureCost) || 0,
      setting: procedureSetting,
    };

    if (editingIndex !== null) {
      const updated = [...procedures];
      updated[editingIndex] = newProcedure;
      setProcedures(updated);
      toast.success(t('builder.procedureUpdated'));
    } else {
      setProcedures([...procedures, newProcedure]);
      toast.success(t('builder.procedureAdded'));
    }

    resetProcedureForm();
  };

  const handleRemoveProcedure = (index: number) => {
    setProcedures(procedures.filter((_, i) => i !== index));
  };

  const handleEditProcedure = (index: number) => {
    const proc = procedures[index];
    setProcedureCode(proc.code);
    setProcedureCodeType(proc.codeType);
    setProcedureDescription(proc.description);
    setProcedurePhase(proc.phase);
    setProcedureQuantity(String(proc.quantity));
    setProcedureCost(String(proc.estimatedCost));
    setProcedureSetting(proc.setting || 'unspecified');
    setEditingIndex(index);
    setShowAddForm(true);
  };

  const handleUpdateProcedurePhase = useCallback((index: number, newPhase: string) => {
    setProcedures(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], phase: newPhase };
      return updated;
    });
  }, []);

  const handleAddSuggestion = (suggestion: BundleSuggestion) => {
    const newProc: LocalScenarioProcedure = {
      code: suggestion.code,
      codeType: suggestion.codeType,
      description: suggestion.description,
      phase: suggestion.phase,
      quantity: suggestion.quantity,
      estimatedCost: suggestion.estimatedCost,
      setting: suggestion.setting,
    };
    setProcedures(prev => [...prev, newProc]);
    toast.success(`Added: ${suggestion.description}`);
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim().toLowerCase()]);
      setTagInput('');
    }
  };

  const handleSave = async (publish: boolean) => {
    if (!name.trim()) {
      toast.error('Please enter a scenario name');
      return;
    }

    setIsSaving(true);
    try {
      const newScenario = scenarioStorage.saveScenario({
        name: name.trim(),
        description: description.trim() || 'No description provided',
        duration: duration.trim() || 'Variable',
        totalProcedures: procedures.length,
        estimatedCost: procedures.reduce((sum, p) => sum + (p.estimatedCost * p.quantity), 0),
        procedures,
        phases: phases.length > 0 ? phases : ['Treatment'],
        tags,
        severityLevel,
        isDraft: false,
        visibility,
        shareToken: '',
        source: templateData ? 'template' : 'manual',
        ...(familyMemberId ? { familyMemberId } : {}),
      });

      const shareUrl = scenarioStorage.getShareUrl(newScenario);

      if (publish) {
        toast.success('Scenario saved & shared!', {
          description: `Share link: ${shareUrl}`,
          duration: 6000,
        });
      } else {
        toast.success('Scenario saved locally!');
      }

      if (onCreated) onCreated(newScenario.id);
      onClose();
    } catch (error) {
      console.error('Error saving scenario:', error);
      toast.error('Failed to save scenario');
    } finally {
      setIsSaving(false);
    }
  };

  const previewSlug = name.trim() ? name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') : 'my-scenario';
  const previewShareUrl = `${window.location.origin}/scenarios/preview/${previewSlug}`;

  return (
    <>
      {/* Title - only show on steps 1 and 3 */}
      {step !== 2 && (
        <div className="mb-6">
          <h2 className={`text-2xl font-bold ${isAhp ? 'text-gray-900' : 'font-mono text-matrix-green'}`}>
            Build Your Scenario
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Step {step} of 3 — {step === 1 ? 'Basic Info' : 'Review & Share'}
          </p>
        </div>
      )}

      {/* Step 1: Basic Info — centered */}
      {step === 1 && (
        <div className="max-w-2xl mx-auto space-y-5">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-sm font-medium">Scenario Name *</Label>
            <Input
              id="name"
              placeholder="e.g., Hip Replacement Surgery, Emergency Appendectomy"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">
              Who is this for? <span className="text-muted-foreground text-xs font-normal">(Optional)</span>
            </Label>
            <FamilyMemberPills
              selectedMemberId={familyMemberId}
              onSelect={setFamilyMemberId}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description" className="text-sm font-medium">Description</Label>
            <Textarea
              id="description"
              placeholder="Describe what this scenario covers..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="text-sm"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="duration" className="text-sm font-medium">Duration</Label>
            <Input
              id="duration"
              placeholder="e.g., 2 weeks, 6 months, 1 year"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Icon & Color</Label>
            <div className="flex gap-3">
              {ICON_OPTIONS.map((option, idx) => {
                const Icon = option.icon;
                return (
                  <button
                    key={idx}
                    onClick={() => setSelectedIconIndex(idx)}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      selectedIconIndex === idx
                        ? isAhp ? 'border-[#2b5e49] bg-green-50' : 'border-matrix-green bg-matrix-green/10'
                        : isAhp ? 'border-gray-200 hover:border-gray-300' : 'border-matrix-green/20 hover:border-matrix-green/40'
                    }`}
                  >
                    <Icon className="w-6 h-6" style={{ color: option.color }} />
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">
              Severity Level
              <span className="text-muted-foreground text-xs ml-2">(1=Routine, 10=Critical)</span>
            </Label>
            <input
              type="range"
              min="1"
              max="10"
              value={severityLevel}
              onChange={(e) => setSeverityLevel(parseInt(e.target.value))}
              className="w-full h-2 bg-gradient-to-r from-green-500 via-yellow-500 to-red-500 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-gray-400 [&::-webkit-slider-thumb]:cursor-pointer"
            />
            <div className="flex justify-between items-center">
              <span className="text-xs text-muted-foreground">
                {severityLevel <= 3 ? 'Simple/Routine' : severityLevel <= 6 ? 'Moderate' : 'High Risk'}
              </span>
              <span className={`text-xl font-mono font-bold ${isAhp ? 'text-[#2b5e49]' : 'text-matrix-green'}`}>
                {severityLevel}/10
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Tags</Label>
            <div className="flex gap-2">
              <Input
                placeholder="e.g., emergency, surgery, outpatient"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddTag(); } }}
                className="flex-1"
              />
              <Button onClick={handleAddTag} variant="outline" size="sm">
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {tags.map(tag => (
                  <Badge key={tag} variant="outline" className="px-2 py-1 text-xs">
                    {tag}
                    <button onClick={() => setTags(tags.filter(t => t !== tag))} className="ml-2 hover:text-destructive">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Step 2: Procedures & Timeline — full-width split-pane */}
      {step === 2 && (
        <div>
          {/* Title for step 2 */}
          <div className="mb-4">
            <h2 className={`text-2xl font-bold ${isAhp ? 'text-gray-900' : 'font-mono text-matrix-green'}`}>
              Procedures & Timeline
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Add procedure codes and arrange them on a timeline
            </p>
          </div>

          <div className="flex gap-6">
            {/* LEFT SIDEBAR */}
            <div className="w-[340px] flex-shrink-0 space-y-4">
              {/* Code search toggle / add form */}
              {!showAddForm && procedures.length > 0 ? (
                <ProcedureFormToggle onClick={() => setShowAddForm(true)} />
              ) : (
                <ProcedureForm
                  procedureCode={procedureCode}
                  setProcedureCode={setProcedureCode}
                  procedureCodeType={procedureCodeType}
                  setProcedureCodeType={setProcedureCodeType}
                  procedureDescription={procedureDescription}
                  setProcedureDescription={setProcedureDescription}
                  procedurePhase={procedurePhase}
                  setProcedurePhase={setProcedurePhase}
                  procedureQuantity={procedureQuantity}
                  setProcedureQuantity={setProcedureQuantity}
                  procedureCost={procedureCost}
                  setProcedureCost={setProcedureCost}
                  procedureSetting={procedureSetting}
                  setProcedureSetting={setProcedureSetting}
                  editingIndex={editingIndex}
                  onAdd={handleAddProcedure}
                  onCancel={resetProcedureForm}
                  onCodeSelected={handleCodeSelected}
                  showCollapse
                  onCollapse={() => { resetProcedureForm(); setShowAddForm(false); }}
                  procedures={procedures}
                />
              )}

              {/* Compact procedure list for sidebar */}
              {procedures.length > 0 && (
                <div className={`rounded-lg border p-3 ${ahpDark ? 'border-[rgba(197,165,90,0.2)] bg-[#0f1d2f]' : isAhp ? 'border-gray-200 bg-white' : 'border-foreground/10 bg-card'}`}>
                  <h4 className={`text-xs font-semibold mb-2 ${ahpDark ? 'text-gray-300' : isAhp ? 'text-gray-700' : 'text-foreground/70'}`}>
                    Added ({procedures.length})
                  </h4>
                  <div className="space-y-1 max-h-[300px] overflow-y-auto">
                    {procedures.map((proc, idx) => (
                      <div
                        key={idx}
                        className={`flex items-center gap-2 text-[11px] px-2 py-1.5 rounded group cursor-pointer transition-colors ${
                          isAhp ? 'hover:bg-gray-50' : 'hover:bg-foreground/5'
                        }`}
                        onClick={() => handleEditProcedure(idx)}
                      >
                        <span className={`font-mono flex-shrink-0 ${isAhp ? 'text-[#2b5e49]' : 'text-matrix-green'}`}>
                          {proc.code}
                        </span>
                        <span className="truncate flex-1 text-muted-foreground">{proc.description}</span>
                        <span className="font-mono flex-shrink-0 text-muted-foreground">
                          ${proc.estimatedCost.toLocaleString()}
                        </span>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleRemoveProcedure(idx); }}
                          className="opacity-0 group-hover:opacity-100 text-destructive flex-shrink-0"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* MAIN AREA */}
            <div className="flex-1 min-w-0 space-y-4">
              {/* Smart Suggestions */}
              <SmartSuggestions
                procedures={procedures}
                onAddSuggestion={handleAddSuggestion}
              />

              {/* Visual Timeline */}
              <ScenarioTimeline
                procedures={procedures}
                phases={phases}
                onRemoveProcedure={handleRemoveProcedure}
                onEditProcedure={handleEditProcedure}
                onUpdateProcedurePhase={handleUpdateProcedurePhase}
              />

              {/* Cost Dashboard */}
              <CostDashboard procedures={procedures} phases={phases} />
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Review & Share — centered */}
      {step === 3 && (
        <div className="max-w-2xl mx-auto space-y-5">
          {/* The payoff: printable comps — national avg + cheapest nearby per
              procedure, so you can negotiate like you would a house price. */}
          {procedures.length > 0 && <NegotiationSheet procedures={procedures} />}

          <ScenarioReviewPanel
            name={name}
            description={description}
            duration={duration}
            severityLevel={severityLevel}
            tags={tags}
            procedures={procedures}
            iconColor={ICON_OPTIONS[selectedIconIndex].color}
          />

          <ScenarioVisibilityToggle
            visibility={visibility}
            onVisibilityChange={setVisibility}
            shareUrl={previewShareUrl}
          />

          <div className={`rounded-lg p-4 ${isAhp ? 'bg-green-50 border border-green-200' : 'bg-blue-500/10 border border-blue-500/20'}`}>
            <p className="text-xs text-muted-foreground">
              Your scenario will be saved to your device. {visibility === 'public'
                ? 'It will be searchable by other users facing the same situation.'
                : 'Only people with the share link can view it.'}
            </p>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex justify-between items-center mt-8 pt-4 border-t border-foreground/10">
        <div>
          {step === 1 ? (
            <Button variant="outline" onClick={onBack}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
          ) : (
            <Button variant="outline" onClick={() => setStep(step - 1)}>
              Back
            </Button>
          )}
        </div>

        <div className="flex gap-2">
          {step < 3 && (
            <Button
              onClick={() => {
                if (step === 1 && !name.trim()) { toast.error('Please enter a scenario name'); return; }
                setStep(step + 1);
              }}
              className={isAhp ? 'bg-[#2b5e49] hover:bg-[#234d3b] text-white' : 'bg-matrix-green text-black hover:bg-matrix-green/90'}
            >
              Next
            </Button>
          )}

          {step === 3 && (
            <>
              <Button
                onClick={() => handleSave(false)}
                variant="outline"
                disabled={isSaving}
              >
                <Save className="w-4 h-4 mr-2" />
                {isSaving ? 'Saving...' : 'Save Locally'}
              </Button>

              <Button
                onClick={() => handleSave(true)}
                className={isAhp ? 'bg-[#2b5e49] hover:bg-[#234d3b] text-white' : 'bg-matrix-green text-black hover:bg-matrix-green/90'}
                disabled={isSaving}
              >
                <Share2 className="w-4 h-4 mr-2" />
                {isSaving ? 'Saving...' : 'Save & Share'}
              </Button>
            </>
          )}
        </div>
      </div>
    </>
  );
}
